import { useEffect, useState } from "react";
import { collection, query, orderBy, onSnapshot } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { OpenOrder } from "../types/orders";

type FSTimestamp = { toMillis?: () => number };

function num(v: unknown, fallback = 0): number {
  if (typeof v === "number") return Number.isFinite(v) ? v : fallback;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  }
  return fallback;
}

function normalizeSide(v: unknown): OpenOrder["side"] {
  const s = String(v ?? "").toLowerCase();
  return s === "buy" || s === "sell" ? (s as OpenOrder["side"]) : "buy";
}

function normalizeStatus(v: unknown): OpenOrder["status"] {
  const s = String(v ?? "").toUpperCase();
  return s === "CLOSED" ? "CLOSED" : "OPEN";
}

function normalizePair(p1?: unknown, p2?: unknown): string {
  const raw = String(p1 ?? p2 ?? "BTC_USDT");
  // accept "BTC/USDT" or "BTC_USDT"; normalize to "BTC_USDT"
  return raw.includes("/") ? raw.replace("/", "/") : raw;
}

function createdAtMillis(v: unknown): number {
  // Firestore Timestamp
  if (v && typeof v === "object" && "toMillis" in (v as FSTimestamp)) {
    const ms = (v as FSTimestamp).toMillis?.();
    if (typeof ms === "number" && Number.isFinite(ms)) return ms;
  }
  // number or ISO/string
  const ms = num(v, NaN);
  if (Number.isFinite(ms)) return ms;
  const d = new Date(String(v));
  if (!Number.isNaN(d.getTime())) return d.getTime();
  return Date.now();
}

export const useUserTrades = () => {
  const [orders, setOrders] = useState<OpenOrder[]>([]);
  const auth = getAuth();
  const db = getFirestore();

  useEffect(() => {
    // react to auth changes to attach/detach listener cleanly
    let unsubSnapshot: (() => void) | null = null;
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      // tear down previous snapshot
      if (unsubSnapshot) {
        unsubSnapshot();
        unsubSnapshot = null;
      }
      if (!user) {
        setOrders([]);
        return;
      }

      const q = query(
        collection(db, "users", user.uid, "trades"),
        orderBy("createdAt", "desc")
      );

      unsubSnapshot = onSnapshot(q, (snapshot) => {
        const mapped: OpenOrder[] = snapshot.docs.map((doc) => {
          const d = doc.data() as any;

          const tradePrice = num(d.tradePrice);
          const tradeAmount = num(d.tradeAmount);
          const priceNow = num(d.priceNow, tradePrice);

          // quantity: if not present, derive from tradeAmount / tradePrice when both are valid
          let quantity = num(d.quantity, NaN);
          if (!Number.isFinite(quantity) && tradePrice > 0) {
            quantity = Number((tradeAmount / tradePrice).toFixed(8));
          }
          if (!Number.isFinite(quantity)) quantity = 0;

          const order: OpenOrder = {
            id: doc.id,
            pair: normalizePair(d.pair, d.crypto),
            side: normalizeSide(d.side ?? d.tradeAction),
            quantity,
            priceNow,
            profitLose: num(d.profitLose, 0),
            tradeAmount,
            tradePrice,
            tradedBy: d.tradedBy ?? "",
            createdAt: createdAtMillis(d.createdAt),
            status: normalizeStatus(d.status),
          };

          return order;
        });

        // client-side sort as a guard in case of mixed data writes
        mapped.sort((a, b) => b.createdAt - a.createdAt);
        setOrders(mapped);
      });
    });

    return () => {
      if (unsubSnapshot) unsubSnapshot();
      unsubAuth();
    };
  }, [auth, db]);

  return orders;
};
